// external imports
import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

// internal imports
import Layout from 'Component/Layouts/layout';
import useApiCaller from 'hooks/apiCaller';
import ClusterManagers from './ClusterManagers';
import ClusterDetails from './ClusterDetails';
import ClusterModals from './ClusterModals';

// map a raw cluster-manager record from the API to the shape the UI expects
const mapClusterManager = (cm) => {
  const regions = cm.regionName ? [cm.regionName] : [];
  return {
    empId: String(cm.employeeId),
    fullName: (cm.employeeName || '').trim(),
    designation: cm.designationName || '',
    location: cm.branchName || '',
    email: cm.officalEmail || '',
    phone: cm.phone || '',
    regions,
    regionsLabel: cm.regionName || cm.branchName || '—',
    trainerCount: cm.trainerCount ?? 0,
    trainers:
      cm.trainers?.map((t) => ({
        empId: String(t.employeeId),
        fullName: (t.employeeName || '').trim(),
        designation: t.designationName || '',
        branchName: t.branchName || '',
        email: t.officalEmail || '',
        regions: t.regionName ? [t.regionName] : [],
      })) || [],
  };
};

const ClusterConfig = () => {
  const apiCaller = useApiCaller();

  // states
  const [clusterManagers, setClusterManagers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selectedEmpId, setSelectedEmpId] = React.useState(null);

  // trainers not yet mapped to any cluster (for the Add Trainer modal)
  const [unassignedTrainers, setUnassignedTrainers] = React.useState([]);

  // modal control — { type: 'add' | 'reassign' | 'move' | null, trainerCount, trainerIds }
  const [modal, setModal] = React.useState({ type: null, trainerCount: 0, trainerIds: [] });
  const [submitting, setSubmitting] = React.useState(false);

  // fetch cluster managers (reused after every add / move / reassign / remove)
  const fetchClusterManagers = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiCaller.get('/super-user/getClusterManagers');
      const details = response?.data?.data?.clusterManagerDetails ?? [];
      setClusterManagers(details.map(mapClusterManager));
    } catch (err) {
      console.error('Error fetching cluster managers:', err);
      setError('Failed to load cluster managers. Please try again.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch cluster managers on mount
  React.useEffect(() => {
    fetchClusterManagers();
  }, [fetchClusterManagers]);

  // fetch unassigned trainers whenever the Add Trainer modal opens
  React.useEffect(() => {
    if (modal.type !== 'add') return;
    let active = true;
    const fetchUnassignedTrainers = async () => {
      try {
        const response = await apiCaller.get('/super-user/getTrainersWithoutCluster');
        const list = response?.data?.data?.trainerWithoutCluster ?? [];
        const mapped = list.map((t) => ({
          empId: String(t.employeeId),
          fullName: (t.employeeName || '').trim(),
          designation: t.designationName || '',
          location: '',
          tag: 'Trainer',
        }));
        if (active) setUnassignedTrainers(mapped);
      } catch (err) {
        console.error('Error fetching unassigned trainers:', err);
        if (active) setUnassignedTrainers([]);
      }
    };
    fetchUnassignedTrainers();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.type]);

  const selectedManager = React.useMemo(
    () => clusterManagers.find((manager) => manager.empId === selectedEmpId) ?? null,
    [clusterManagers, selectedEmpId]
  );

  const closeModal = () => setModal({ type: null, trainerCount: 0, trainerIds: [] });

  // POST to the shared add/reassign/remove/move endpoint, then refresh the list
  const postTrainerAction = async (payload) => {
    try {
      setSubmitting(true);
      await apiCaller.post('/super-user/add-reassign-trainers', payload);
      await fetchClusterManagers();
      closeModal();
    } catch (err) {
      console.error('Error updating trainers:', err);
      // keep the modal open so the user can retry
    } finally {
      setSubmitting(false);
    }
  };

  // add / reassign / move flows from the shared modal
  const handleConfirm = ({ mode, employeeIds, targetCMId }) => {
    if (!selectedManager) return;

    if (mode === 'add') {
      postTrainerAction({
        reqType: 'add',
        trainerId: (employeeIds || []).map(Number),
        clusterManagerId: Number(selectedManager.empId),
      });
    } else if (mode === 'reassign') {
      postTrainerAction({
        reqType: 'reassign',
        clusterManagerId: Number(targetCMId),
        fromClusterManagerId: Number(selectedManager.empId),
        trainerId: [0], // 0 → move every trainer under the source CM
      });
    } else if (mode === 'move') {
      postTrainerAction({
        reqType: 'move',
        trainerId: (modal.trainerIds || []).map(Number),
        clusterManagerId: Number(targetCMId),
      });
    }
  };

  // remove (unmap) a single trainer from the current cluster manager
  const handleDeleteTrainer = (trainerEmpId) => {
    if (!selectedManager) return;
    postTrainerAction({
      reqType: 'remove',
      trainerId: Number(trainerEmpId),
      clusterManagerId: Number(selectedManager.empId),
    });
  };

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Professional Header */}
        <Box
          sx={{
            mb: 2,
            background: '#ffffff',
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography variant="h5" fontWeight="600" sx={{ color: '#1e293b', mb: 0.25 }}>
                Cluster Configuration
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: '400' }}>
                Pick a cluster manager on the left to manage their trainers.
              </Typography>
            </Box>
          </Box>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Box
            sx={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              p: 2,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ color: '#b91c1c', fontSize: 14 }}>{error}</Typography>
          </Box>
        )}

        {!loading && !error && (
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Left — cluster managers list */}
            <ClusterManagers
              managers={clusterManagers}
              selectedEmpId={selectedEmpId}
              onSelect={setSelectedEmpId}
            />

            {/* Right — selected cluster manager details */}
            <ClusterDetails
              manager={selectedManager}
              onAddTrainer={() => setModal({ type: 'add', trainerCount: 0, trainerIds: [] })}
              onReassignAll={() => setModal({ type: 'reassign', trainerCount: 0, trainerIds: [] })}
              onMove={(ids) =>
                setModal({ type: 'move', trainerCount: ids.length, trainerIds: ids })
              }
              onDeleteTrainer={handleDeleteTrainer}
            />
          </Box>
        )}
      </Box>

      {/* Shared modal for add / reassign / move flows */}
      <ClusterModals
        open={!!modal.type}
        mode={modal.type}
        manager={selectedManager}
        managers={clusterManagers}
        employees={[]}
        unassignedTrainers={unassignedTrainers}
        selectedTrainerCount={modal.trainerCount}
        submitting={submitting}
        onClose={closeModal}
        onConfirm={handleConfirm}
      />
    </Layout>
  );
};

export default ClusterConfig;
